import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiAppLegacy } from '@/shared/api';

/**
 * 발전소 용도/계약 수정 입력 — 정보 상세 → 용도변경/계약수정 폼. `contractDetail` 은 wire object(JSON)로 그대로
 * 전송(읽기측은 문자열 파싱, 쓰기측은 object — 구앱 정합). 계약명(contractName)은 BE 파생이라 미전송.
 */
export interface UpdatePlantContractInput {
  plantId: string;
  /** 용도 — '01' 발전사업 · '02' 자가소비. */
  useCode: string;
  /** 계약방식/계약종별 코드(P101/P102/P103/F2xx…). 미선택 null. */
  contractCode: string | null;
  /** format 'yyyy-MM' — 신규 계약만 이번 달 전송. 기존 편집은 null(미전송) → BE 저장값 보존(구앱 정합). */
  contractAt: string | null;
  /** 계약 상세(발전사업 고정단가 `{ctrtStDate,ctrtEdDate,recRate,unitPrice}` / 현물 `{recRate}`). 자가소비/미상세 null. */
  contractDetail: Record<string, unknown> | null;
}

/**
 * 발전소 용도/계약 수정 mutation — `PUT /plants/{id}/profile`(계약만 부분 전송). 성공 시 발전소 목록·프로필·수익
 * 캐시 무효화(계약 변경이 수익 displayType·집계에 파급). `toRaw` 정책상 wire 합성은 훅 inline([[feedback_no_toraw_mapper]]).
 */
export function useUpdatePlantContractMutation() {
  const api = useApiAppLegacy();
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdatePlantContractInput>({
    mutationFn: async ({ plantId, useCode, contractCode, contractAt, contractDetail }) => {
      await api.plant.updateProfile(Number(plantId), {
        // contractAt 은 신규(non-null)일 때만 포함 — 기존 편집은 생략해 BE 저장 계약월을 보존.
        profile: {
          contract: {
            useCode,
            contractCode,
            contractDetail,
            ...(contractAt != null && { contractAt }),
          },
        },
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['plants'] });
      void queryClient.invalidateQueries({ queryKey: ['plant'] });
      void queryClient.invalidateQueries({ queryKey: ['revenues'] });
    },
  });
}
